/** Official imagery with graceful fallbacks (initials / team colour) — never a broken image. */
import { useState, type CSSProperties } from 'react';
import { headshotUrl, imageFailed, markImageFailed, nameInitials, normalizeTricode, teamLogoUrl, type ImageSource } from '../media/assets';
import { teamAccent } from './color';

export function Headshot({
  personId,
  source,
  name,
  size = 40,
  ring,
  className = '',
}: {
  personId?: number;
  source?: ImageSource;
  name?: string;
  size?: number;
  ring?: string;
  className?: string;
}) {
  const url = headshotUrl(personId, source);
  const [failedUrl, setFailedUrl] = useState<string>();
  const [loadedUrl, setLoadedUrl] = useState<string>();
  const bad = imageFailed(url) || !!url && failedUrl === url;
  const loaded = !bad && loadedUrl === url;
  const style = { '--size': `${size}px`, '--ring': ring ?? 'var(--line-strong)' } as CSSProperties;
  return (
    <span className={`headshot${bad ? ' is-fallback' : ''} ${className}`} style={style} aria-hidden="true">
      {!loaded && <b>{nameInitials(name)}</b>}
      {!bad && url && (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          style={{ opacity: loaded ? 1 : 0 }}
          onLoad={() => setLoadedUrl(url)}
          onError={() => {
            markImageFailed(url);
            setFailedUrl(url);
          }}
        />
      )}
    </span>
  );
}

export function TeamLogo({ tricode, teamId, size = 28, className = '' }: { tricode: string; teamId?: number; size?: number; className?: string }) {
  const url = teamLogoUrl({ tricode, ...(teamId !== undefined ? { teamId } : {}) });
  const [failedUrl, setFailedUrl] = useState<string>();
  const bad = imageFailed(url) || !!url && failedUrl === url;
  const code = normalizeTricode(tricode);
  const style = { '--size': `${size}px`, '--team': safeAccent(code) } as CSSProperties;
  return (
    <span className={`logo${bad ? ' is-fallback' : ''} ${className}`} style={style} aria-hidden="true">
      {!bad && url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => {
            markImageFailed(url);
            setFailedUrl(url);
          }}
        />
      ) : (
        <b>{code}</b>
      )}
    </span>
  );
}

function safeAccent(code: string): string {
  try {
    return teamAccent(code);
  } catch {
    return '#888';
  }
}
